import { Table, TableBody } from '@components/design/migrated/Table';
import React, { useCallback, useState } from 'react';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { ComponentName, DefaultSortingOptionsForProducts } from '@models/Products';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import { PageName, PageType } from '@utils/pageConfig/Types';
import * as tableStyles from '@utils/tableViewscss/TableViewBoard.module.scss';
import { ProductsCompareModalTableProps } from '../Types';
import { getFirstActivityRow, getModelNamesColumn } from '../utils/ProductsModelCompareUtils';
import ProductsCompareModalDataRows from './products-compare-modal-data-rows/ProductsCompareModalDataRows';
import ProductsCompareModalFeatureRows from './products-compare-modal-feature-rows/ProductsCompareModalFeatureRows';
import ProductsCompareModalFirstActivityRow from './products-compare-modal-first-activity-row/ProductsCompareModalFirstActivityRow';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

const ProductsCompareModalTable: React.FC<ProductsCompareModalTableProps> = ({
  productsCompareKpi,
  isLoading,
  currency,
  isApiError = false,
}) => {
  const pagesConfig = usePageConfig();
  const pageElementViewParam = useGeneratePageElementViewParam();
  const { ref } = useInViewTracking(
    () => ({
      ...pageElementViewParam,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT,
      componentName: ComponentName.PRODUCT_LEADERBOARD_COMPARE_MODELS,
      elementType: ElementType.TABLE,
      access: pagesConfig.access,
    }),
    { threshold: 0.2, triggerOnce: true },
  );

  const firstActivityRow = getFirstActivityRow(productsCompareKpi);
  const columns = getModelNamesColumn(productsCompareKpi);

  const [tableHeadHeight, setTableHeadHeight] = useState<number>(0);

  const onTableHeadRendered = useCallback(
    (elementRef: HTMLElement) => {
      setTableHeadHeight(elementRef.offsetHeight);
    },
    [setTableHeadHeight],
  );

  return renderVisualization({
    isLoading,
    fetchedApiData: productsCompareKpi,
    defaultData: [],
    jsxToRender: (
      <div ref={ref}>
        <Table
          dontWrap={true}
          columns={columns}
          isHeaderSticky={true}
          defaultSortingOptions={DefaultSortingOptionsForProducts}
          className={tableStyles.TableViewboard__Table}
          onRender={onTableHeadRendered}
        >
          <TableBody>
            <ProductsCompareModalFirstActivityRow firstActivityRow={firstActivityRow} />
            <ProductsCompareModalDataRows
              productsCompareKpi={productsCompareKpi}
              currency={currency}
              headerOffset={tableHeadHeight}
            />
            <ProductsCompareModalFeatureRows productsCompareKpi={productsCompareKpi} headerOffset={tableHeadHeight} />
          </TableBody>
        </Table>
      </div>
    ),
    isError: isApiError,
  });
};

export default ProductsCompareModalTable;
